import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MinusSquareOutlined, PlusSquareOutlined } from '@ant-design/icons'
import { Input, notification, Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import {
  UserGroup,
  UserGroupRepository,
  UserGroupService
} from 'domain/building'
import { UserEntity } from 'domain/user/UserEntity'
import { useBuildingUsersAndGroups } from 'hooks/useBuildingUsersAndGroups'
import { mutate } from 'swr'
import Utils from 'utils'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

type Props = {
  open: boolean
  buildingId?: string | number
  onClose: () => void
}

export default function ModalManageUserGroups({
  open,
  buildingId,
  onClose
}: Props) {
  const { t } = useTranslation()
  const bId = buildingId ? String(buildingId) : ''

  const {
    userList = [],
    userGroups = [],
    isLoadingGroups,
    refetchGroups,
    getRoleString
  } = useBuildingUsersAndGroups(bId)

  const [mode, setMode] = useState<'list' | 'editor'>('list')
  const [editingGroup, setEditingGroup] = useState<UserGroup | null>(null)
  const [deletingGroup, setDeletingGroup] = useState<UserGroup | null>(null)
  const [groupName, setGroupName] = useState('')
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [userSearchText, setUserSearchText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])

  const toggleExpandGroup = (groupId: number | string) => {
    setExpandedRowKeys((prev) => (prev.includes(groupId) ? [] : [groupId]))
  }

  const userEntities = useMemo(
    () => (userList || []).map(UserEntity.from),
    [userList]
  )

  const renderExpandedRow = useCallback(
    (g: any) => {
      const members = userEntities.filter((u: UserEntity) =>
        g.userIds.includes(u.id)
      )
      return (
        <div className="py-2.5 px-4 bg-slate-50 border-t border-slate-100 flex flex-col gap-2">
          <span className="text-xs text-slate-600 font-bold">
            メンバー({members.length}名)
          </span>
          <div className="flex flex-wrap gap-1.5 items-center">
            {members.length > 0 ? (
              members.map((u: UserEntity) => (
                <Tag
                  key={u.id}
                  className="text-[11px] bg-white border-slate-200 m-0"
                >
                  {u.getFullName()}
                </Tag>
              ))
            ) : (
              <span className="text-slate-400 text-xs">メンバーなし</span>
            )}
          </div>
        </div>
      )
    },
    [userEntities]
  )

  const groupEntities = useMemo(
    () => (userGroups || []).map(UserGroup.from),
    [userGroups]
  )

  const sortedGroupEntities = useMemo(
    () => UserGroupService.sortGroupsByCreatedAt(groupEntities, 'desc'),
    [groupEntities]
  )

  const handleOpenCreate = () => {
    setEditingGroup(null)
    setGroupName('')
    setSelectedUserIds([])
    setUserSearchText('')
    setMode('editor')
  }

  const handleOpenEdit = (group: UserGroup) => {
    setEditingGroup(group)
    setGroupName(group.groupName)
    setSelectedUserIds(group.userIds)
    setUserSearchText('')
    setMode('editor')
  }

  const handleBackToList = () => {
    setMode('list')
    setEditingGroup(null)
    setGroupName('')
    setSelectedUserIds([])
  }

  const handleToggleUserSelection = (userId: number) => {
    setSelectedUserIds((prev) =>
      UserGroupService.toggleUserSelection(prev, userId)
    )
  }

  const handleSelectAllFilteredUsers = (filteredUsers: UserEntity[]) => {
    setSelectedUserIds((prev) =>
      UserGroupService.toggleAllFilteredUsers(prev, filteredUsers)
    )
  }

  const handleSaveGroup = async () => {
    const validation = UserGroupService.validateGroupPayload(
      groupName,
      selectedUserIds
    )

    if (!validation.isValid) {
      let msg = t('group.membersRequired', 'メンバーを1人以上選択してください')
      if (validation.errorKey === 'nameRequired') {
        msg = t('group.nameRequired', 'グループ名を入力してください')
      } else if (validation.errorKey === 'nameTooLong') {
        msg = t(
          'group.nameTooLong',
          'グループ名は100文字以内で入力してください'
        )
      }
      notification.error({ key: 'group-validation-error', message: msg })
      return
    }

    const payload = {
      group_name: groupName.trim(),
      member_ids: selectedUserIds
    }

    setIsSubmitting(true)
    try {
      if (editingGroup) {
        await UserGroupRepository.updateUserGroup(bId, editingGroup.id, payload)
      } else {
        await UserGroupRepository.createUserGroup(bId, payload)
      }

      await refetchGroups()
      await mutate(`/v1/buildings/${bId}/user-groups`)

      notification.success({
        key: 'save-group-success',
        message: editingGroup
          ? t('group.updateSuccess', 'グループを更新しました')
          : t('group.createSuccess', 'グループを作成しました')
      })

      handleBackToList()
    } catch (err: unknown) {
      Utils.handleError(err, t, undefined, 'group.saveError')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmDeleteGroup = async () => {
    if (!deletingGroup) return
    setIsDeleting(true)
    try {
      await UserGroupRepository.deleteUserGroup(bId, deletingGroup.id)
      await refetchGroups()
      await mutate(`/v1/buildings/${bId}/user-groups`)
      notification.success({
        key: 'delete-group-success',
        message: t('group.deleteSuccess', 'グループを削除しました')
      })
      setDeletingGroup(null)
    } catch (err: unknown) {
      Utils.handleError(err, t, undefined, 'group.deleteError')
    } finally {
      setIsDeleting(false)
    }
  }

  const filteredUsers = useMemo(() => {
    if (!userSearchText.trim()) return userEntities
    const query = userSearchText.trim().toLowerCase()
    return userEntities.filter(
      (u: UserEntity) =>
        u.getFullName().toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query) ||
        u.getOrganizationName().toLowerCase().includes(query) ||
        getRoleString(u).toLowerCase().includes(query)
    )
  }, [userEntities, userSearchText, getRoleString])

  let modalTitle = 'ユーザーグループ設定'
  if (mode === 'editor') {
    modalTitle = editingGroup ? 'グループ編集' : '新規グループ作成'
  }

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        width="720px"
        rootClassName="shared-permission-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <Title title={modalTitle} />
          {mode === 'editor' ? (
            <Button
              sizing="h-8 px-3"
              className="text-xs"
              onClick={handleBackToList}
            >
              ← 一覧に戻る
            </Button>
          ) : (
            <Button
              type="primary"
              sizing="h-8 px-4"
              className="theme-bg-primary text-xs font-bold"
              onClick={handleOpenCreate}
            >
              + 新規グループ作成
            </Button>
          )}
        </div>

        {mode === 'list' && (
          <div>
            <Table
              dataSource={sortedGroupEntities}
              rowKey={(g: any) => g.id}
              loading={isLoadingGroups}
              pagination={false}
              scroll={{ y: 320 }}
              size="small"
              bordered
              expandable={{
                expandedRowKeys,
                onExpandedRowsChange: (keys) => {
                  const latestKey = keys[keys.length - 1]
                  setExpandedRowKeys(latestKey ? [latestKey] : [])
                },
                showExpandColumn: false,
                expandedRowRender: renderExpandedRow
              }}
              columns={[
                {
                  title: 'グループ名',
                  dataIndex: 'groupName',
                  key: 'groupName',
                  width: 170,
                  render: (_, g: any) => {
                    const isExpanded = expandedRowKeys.includes(g.id)
                    return (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          className="text-[#3F63B6] hover:text-blue-700 cursor-pointer border-0 bg-transparent flex items-center justify-center p-0 text-sm"
                          onClick={() => toggleExpandGroup(g.id)}
                        >
                          {isExpanded ? (
                            <MinusSquareOutlined />
                          ) : (
                            <PlusSquareOutlined />
                          )}
                        </button>
                        <span className="font-bold text-[#3F63B6]">
                          {g.groupName}
                        </span>
                      </div>
                    )
                  }
                },
                {
                  title: 'メンバー',
                  key: 'members',
                  width: 90,
                  render: (_, g: any) => {
                    const members = userEntities.filter((u: UserEntity) =>
                      g.userIds.includes(u.id)
                    )
                    return (
                      <span className="text-xs font-semibold text-slate-700">
                        {members.length}名
                      </span>
                    )
                  }
                },
                {
                  title: '作成日時',
                  key: 'createdAt',
                  width: 140,
                  render: (_, g: UserGroup) => (
                    <span className="text-xs text-slate-600">
                      {g.createdAt
                        ? dayjs(g.createdAt).format('YYYY/MM/DD HH:mm')
                        : '-'}
                    </span>
                  )
                },
                {
                  title: '操作',
                  key: 'actions',
                  width: 110,
                  align: 'center',
                  render: (_, g: any) => (
                    <div className="flex justify-center items-center gap-2">
                      <button
                        type="button"
                        className="text-xs font-semibold text-sky-600 hover:text-sky-800 cursor-pointer border-0 bg-transparent"
                        onClick={() => handleOpenEdit(g)}
                      >
                        編集
                      </button>
                      <button
                        type="button"
                        className="text-xs font-semibold text-rose-600 hover:text-rose-800 cursor-pointer border-0 bg-transparent"
                        onClick={() => setDeletingGroup(g)}
                      >
                        削除
                      </button>
                    </div>
                  )
                }
              ]}
            />
            <div className="flex justify-end mt-4 pt-3 border-0 border-t border-solid border-[#EEEFF2]">
              <Button sizing="w-[120px] h-[38px]" onClick={onClose}>
                閉じる
              </Button>
            </div>
          </div>
        )}

        {mode === 'editor' && (
          <div className="space-y-4">
            <div>
              <span className="block text-xs font-bold text-slate-700 mb-1">
                グループ名 <span className="text-rose-500">*</span>
              </span>
              <Input
                placeholder="例: 設計チーム, 施工管理者"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                className="h-9 text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="block text-xs font-bold text-slate-700">
                  メンバー選択 ({selectedUserIds.length}名選択中)
                </span>
                <Input
                  placeholder="ユーザー名・メールで検索"
                  value={userSearchText}
                  onChange={(e) => setUserSearchText(e.target.value)}
                  allowClear
                  className="w-56 h-8 text-xs"
                />
              </div>

              <Table
                dataSource={filteredUsers}
                rowKey={(u: any) => u.id}
                pagination={false}
                scroll={{ y: 240 }}
                size="small"
                bordered
                columns={[
                  {
                    title: (
                      <input
                        type="checkbox"
                        checked={
                          filteredUsers.length > 0 &&
                          filteredUsers.every((u: UserEntity) =>
                            selectedUserIds.includes(u.id)
                          )
                        }
                        onChange={() =>
                          handleSelectAllFilteredUsers(filteredUsers)
                        }
                        className="cursor-pointer accent-blue-600"
                      />
                    ),
                    key: 'select',
                    width: 40,
                    align: 'center',
                    render: (_, u: any) => (
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(u.id)}
                        onChange={() => handleToggleUserSelection(u.id)}
                        className="cursor-pointer accent-blue-600"
                      />
                    )
                  },
                  {
                    title: '名前',
                    key: 'name',
                    render: (_, u: any) => (
                      <span className="font-semibold text-slate-800 text-xs">
                        {u.getFullName()}
                      </span>
                    )
                  },
                  {
                    title: '組織名',
                    key: 'org',
                    render: (_, u: any) => (
                      <span className="text-xs text-slate-600">
                        {u.getOrganizationName()}
                      </span>
                    )
                  },
                  {
                    title: 'メールアドレス',
                    dataIndex: 'email',
                    key: 'email',
                    render: (email: string) => (
                      <span className="text-xs text-slate-500">
                        {email || '-'}
                      </span>
                    )
                  },
                  {
                    title: '権限',
                    key: 'role',
                    width: 100,
                    render: (_, u: any) => (
                      <Tag className="text-xs m-0">{getRoleString(u)}</Tag>
                    )
                  }
                ]}
              />
            </div>

            <div className="flex justify-end items-center gap-3 pt-3 border-0 border-t border-solid border-[#EEEFF2]">
              <Button sizing="w-[110px] h-[38px]" onClick={handleBackToList}>
                キャンセル
              </Button>
              <Button
                type="primary"
                sizing="w-[120px] h-[38px]"
                className="theme-bg-primary font-bold"
                onClick={handleSaveGroup}
                disabled={isSubmitting}
              >
                {editingGroup ? '更新する' : '作成する'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {deletingGroup && (
        <Modal
          open={!!deletingGroup}
          onCancel={() => setDeletingGroup(null)}
          width="480px"
          rootClassName="shared-permission-modal"
        >
          <Title title={t('common.deleteConfirm', '削除の確認')} />
          <div className="flex flex-col gap-6 text-center my-4">
            <Typography className="text-sm font-medium text-slate-700">
              {t(
                'group.deleteConfirmation',
                'グループを削除してもよろしいですか？'
              )}
            </Typography>
            <div className="bg-[#F9FBFF] py-3 px-4 rounded-lg border border-solid border-slate-100 mx-auto w-[360px]">
              <span className="text-base font-bold text-slate-800">
                {deletingGroup.groupName}
              </span>
            </div>
            <div className="flex justify-center gap-6 mt-2">
              <Button
                sizing="w-[140px] h-[40px]"
                onClick={() => setDeletingGroup(null)}
              >
                {t('common.cancel', 'キャンセル')}
              </Button>
              <Button
                sizing="w-[140px] h-[40px]"
                className="bg-[#B80808]"
                onClick={handleConfirmDeleteGroup}
                disabled={isDeleting}
              >
                {t('common.delete', '削除')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  )
}

ModalManageUserGroups.defaultProps = {
  buildingId: undefined
}
